import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Search } from 'lucide-react';
import {
  enhanceQueryWithTemporalContext,
  isDateRelatedQuery,
  extractTemporalKeywords
} from '../../utils/sharedResponseUtils';
import SmartMarkdown from '../../components/SmartMarkdown/SmartMarkdown';
import '../../components/SmartMarkdown/SmartMarkdown.css';
import ThinkingProcess from '../../components/ThinkingProcess/ThinkingProcess';
import ActionApproval from '../../components/ActionApproval/ActionApproval';
import ActionExecutionResults from '../../components/ActionExecutionResults/ActionExecutionResults';
import EntityDataCards from '../../components/EntityDataCards/EntityDataCards';
import EntityDataList from '../../components/EntityDataList/EntityDataList';

const SalesBuddy = () => {
  console.log('🚀 SalesBuddy component is rendering');
  
  const { getCurrentTenant, user } = useAuth();
  const { salesBuddyNotifications } = useNotifications();
  const { getProtectedCollectionName, getProtectedTenantInfo } = useTenantProtection();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  
  console.log('👤 User context:', user);
  console.log('🏢 Tenant info:', getProtectedTenantInfo());
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ragEnabled, setRagEnabled] = useState(true); // RAG enabled by default
  const [strictRagMode, setStrictRagMode] = useState(false); // Strict RAG mode (no bypasses)
  const [aiConfig, setAiConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [showSalesNotePopup, setShowSalesNotePopup] = useState(false);
  const [showCustomerPopup, setShowCustomerPopup] = useState(false);
  const [showActivityPopup, setShowActivityPopup] = useState(false);
  const [showLeadPopup, setShowLeadPopup] = useState(false);
  const [showOpportunityPopup, setShowOpportunityPopup] = useState(false);
  const [showAttachmentPopup, setShowAttachmentPopup] = useState(false);
  const [showLineQrModal, setShowLineQrModal] = useState(false);
  const [lineProfile, setLineProfile] = useState(null);
  const [lineProfileLoading, setLineProfileLoading] = useState(false);
  const [showQuestionPanel, setShowQuestionPanel] = useState(false);
  const chatboxMessagesRef = useRef(null);
  const previousStorageKeyRef = useRef(null);
  const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);
  const [pendingActions, setPendingActions] = useState(null);
  const [isExecutingActions, setIsExecutingActions] = useState(false);
  const [taskSessionId, setTaskSessionId] = useState(null); // Task session ID for sequential actions
  const processedSalesBuddyNotifications = useRef(new Set());

  // formatMessageContent function removed - now handled by SmartMarkdown component


  // Position state - supports 'top', 'bottom', 'left', 'right'
  const [position, setPosition] = useState(() => {
    // Load saved position from localStorage
    const saved = localStorage.getItem('sales-buddy-position');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Validate position value
        if (['top', 'bottom', 'left', 'right'].includes(parsed)) {
          return parsed;
        }
      } catch (e) {
        console.warn('Failed to parse saved position:', e);
      }
    }
    // Check for legacy format (boolean isOnRight)
    const legacySaved = localStorage.getItem('sales-buddy-side');
    if (legacySaved) {
      try {
        const isRight = JSON.parse(legacySaved);
        return isRight ? 'right' : 'left';
      } catch (e) {
        console.warn('Failed to parse legacy position:', e);
      }
    }
    return 'right'; // Default to right side
  });
  
  // Ribbon/minimized state
  const [isMinimized, setIsMinimized] = useState(() => {
    const saved = localStorage.getItem('sales-buddy-minimized');
    return saved ? JSON.parse(saved) : false; // Default to not minimized
  });
  
  // Chatbox width state for desktop resizing
  const [chatboxWidth, setChatboxWidth] = useState(() => {
    const saved = localStorage.getItem('sales-buddy-width');
    return saved ? parseInt(saved, 10) : 384; // Default 384px
  });
  
  // Check if device is mobile
  const [isMobile, setIsMobile] = useState(() => {
    return window.innerWidth <= 768;
  });
  
  const [isResizing, setIsResizing] = useState(false);
  
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const isResizingRef = useRef(false);

  // Using shared utilities from sharedResponseUtils
  // Build a stable storage key per tenant and user
  const getStorageKey = () => {
    const tenant = getProtectedTenantInfo();
    const tenantCode = tenant?.code || 'DEFAULT';
    const userId = user?.id || user?._id || 'anonymous';
    return `sales-buddy-history:${tenantCode}:${userId}`;
  };

  // Load persisted messages when tenant/user context becomes available
  useEffect(() => {
    try {
      const key = getStorageKey();
      previousStorageKeyRef.current = key;
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setMessages(parsed);
        }
      }
    } catch (e) {
      console.warn('Failed to load persisted SalesBuddy messages:', e);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?._id, getProtectedTenantInfo()?.code]);

  // Append SalesBuddy notifications into chat stream
  useEffect(() => {
    if (!salesBuddyNotifications || salesBuddyNotifications.length === 0) {
      return;
    }

    const newMessages = [];
    salesBuddyNotifications.forEach((notification) => {
      const notificationKey = notification.notificationId ||
        notification.id ||
        `${notification.createdAt || Date.now()}-${notification.title || 'notification'}`;

      if (processedSalesBuddyNotifications.current.has(notificationKey)) {
        return;
      }

      processedSalesBuddyNotifications.current.add(notificationKey);

      const title = notification.title || 'Notification';
      const message = notification.message || '';
      const content = message ? `${title}\n${message}` : title;

      newMessages.push({
        role: 'assistant',
        content,
        metadata: {
          source: 'notification',
          notificationId: notification.notificationId || notification.id || null,
          channel: 'salesbuddy'
        }
      });
    });

    if (newMessages.length > 0) {
      setMessages(prev => [...prev, ...newMessages]);
    }
  }, [salesBuddyNotifications]);

  // Persist messages to localStorage on change
  useEffect(() => {
    try {
      const key = getStorageKey();
      localStorage.setItem(key, JSON.stringify(messages));
    } catch (e) {
      console.warn('Failed to persist SalesBuddy messages:', e);
    }
  }, [messages]);

  // Clear persisted messages on logout
  useEffect(() => {
    // When user becomes null/undefined, clear the last known storage key
    if (!user) {
      const lastKey = previousStorageKeyRef.current;
      if (lastKey) {
        try { localStorage.removeItem(lastKey); } catch (_) {}
      }
      // Also clear in-memory messages
      setMessages([]);
    }
  }, [user]);


  // Helper function to get tenant-specific collection name (with protection)
  const getTenantCollectionName = () => {
    return getProtectedCollectionName('CRM');
  };

  // Helper function to render note links
  const renderSalesNoteLinks = (contextSources) => {
    if (!contextSources || !Array.isArray(contextSources)) return null;
    
    const salesNoteSources = contextSources.filter(source => 
      source.type === 'sales_note' && source.metadata?.entity_id
    );
    
    if (salesNoteSources.length === 0) return null;
    
    const handleSalesNoteClick = (entityId) => {
      // Navigate to notes page with the specific note ID
      // This preserves the tenant context
      navigate(`/notes?noteId=${entityId}`);
    };
    
    return (
      <div className="mt-3 relative">
        <button
          onClick={() => setShowSalesNotePopup(prev => !prev)}
          className="sales-note-popup inline-flex items-center px-2 py-1 text-xs bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white rounded transition-colors duration-200 cursor-pointer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Note
        </button>
        
        {showSalesNotePopup && (
          <div className="sales-note-popup absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-48">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Related Notes
                </span>
                <button
                  onClick={() => setShowSalesNotePopup(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {salesNoteSources.map((source, index) => (
                <div key={index} className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {source.content.substring(0, 40)}
                        {source.content.length > 40 ? '...' : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        handleSalesNoteClick(source.metadata.entity_id);
                        setShowSalesNotePopup(false);
                      }}
                      className="ml-2 px-2 py-1 text-xs bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white rounded transition-colors duration-200 cursor-pointer"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Helper function to render customer links
  const renderCustomerLinks = (contextSources) => {
    if (!contextSources || !Array.isArray(contextSources)) return null;
    
    const customerSources = contextSources.filter(source => 
      source.type === 'customer' && source.metadata?.entity_id
    );
    
    if (customerSources.length === 0) return null;
    
    const handleCustomerClick = (entityId) => {
      // Navigate to customers page with the specific customer ID
      // This preserves the tenant context
      navigate(`/customers?customerId=${entityId}`);
    };
    
    return (
      <div className="mt-3 relative">
        <button
          onClick={() => setShowCustomerPopup(prev => !prev)}
          className="customer-popup inline-flex items-center px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded transition-colors duration-200 cursor-pointer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Customer
        </button>
        
        {showCustomerPopup && (
          <div className="customer-popup absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-48">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Related Customers
                </span>
                <button
                  onClick={() => setShowCustomerPopup(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {customerSources.map((source, index) => (
                <div key={index} className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {source.content.substring(0, 35)}
                        {source.content.length > 35 ? '...' : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        handleCustomerClick(source.metadata.entity_id);
                        setShowCustomerPopup(false);
                      }}
                      className="ml-2 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded transition-colors duration-200 cursor-pointer"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Helper function to render activity links
  const renderActivityLinks = (contextSources) => {
    if (!contextSources || !Array.isArray(contextSources)) return null;
    
    const activitySources = contextSources.filter(source => 
      source.type === 'activity' && source.metadata?.entity_id
    );
    
    if (activitySources.length === 0) return null;
    
    const handleActivityClick = (entityId) => {
      // Navigate to activities page with the specific activity ID
      // This preserves the tenant context
      navigate(`/activities?activityId=${entityId}`);
    };
    
    return (
      <div className="mt-3 relative">
        <button
          onClick={() => setShowActivityPopup(prev => !prev)}
          className="activity-popup inline-flex items-center px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded transition-colors duration-200 cursor-pointer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Activity
        </button>
        
        {showActivityPopup && (
          <div className="activity-popup absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-48">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Related Activities
                </span>
                <button
                  onClick={() => setShowActivityPopup(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {activitySources.map((source, index) => (
                <div key={index} className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-900 dark:text-white font-medium">
                        {source.metadata?.subject || 'Unknown Activity'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {source.content.substring(0, 40)}
                        {source.content.length > 40 ? '...' : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        handleActivityClick(source.metadata.entity_id);
                        setShowActivityPopup(false);
                      }}
                      className="ml-2 px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded transition-colors duration-200 cursor-pointer"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Helper function to render lead links
  const renderLeadLinks = (contextSources) => {
    if (!contextSources || !Array.isArray(contextSources)) return null;
    
    const leadSources = contextSources.filter(source => 
      source.type === 'lead' && source.metadata?.entity_id
    );
    
    if (leadSources.length === 0) return null;
    
    const handleLeadClick = (entityId) => {
      // Navigate to leads page with the specific lead ID
      // This preserves the tenant context
      navigate(`/leads?leadId=${entityId}`);
    };
    
    return (
      <div className="mt-3 relative">
        <button
          onClick={() => setShowLeadPopup(prev => !prev)}
          className="lead-popup inline-flex items-center px-2 py-1 text-xs bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white rounded transition-colors duration-200 cursor-pointer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          Lead
        </button>
        
        {showLeadPopup && (
          <div className="lead-popup absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-48">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Related Leads
                </span>
                <button
                  onClick={() => setShowLeadPopup(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {leadSources.map((source, index) => (
                <div key={index} className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-900 dark:text-white font-medium">
                        {source.metadata?.contact_name || 'Unknown Lead'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {source.content.substring(0, 40)}
                        {source.content.length > 40 ? '...' : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        handleLeadClick(source.metadata.entity_id);
                        setShowLeadPopup(false);
                      }}
                      className="ml-2 px-2 py-1 text-xs bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white rounded transition-colors duration-200 cursor-pointer"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Helper function to render opportunity links
  const renderOpportunityLinks = (contextSources) => {
    if (!contextSources || !Array.isArray(contextSources)) return null;
    
    const opportunitySources = contextSources.filter(source => 
      source.type === 'opportunity' && source.metadata?.entity_id
    );
    
    if (opportunitySources.length === 0) return null;
    
    const handleOpportunityClick = (entityId) => {
      // Navigate to opportunities page with the specific opportunity ID
      // This preserves the tenant context
      navigate(`/opportunities?opportunityId=${entityId}`);
    };
    
    return (
      <div className="mt-3 relative">
        <button
          onClick={() => setShowOpportunityPopup(prev => !prev)}
          className="opportunity-popup inline-flex items-center px-2 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded transition-colors duration-200 cursor-pointer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
          Opportunity
        </button>
        
        {showOpportunityPopup && (
          <div className="opportunity-popup absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-48">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  Related Opportunities
                </span>
                <button
                  onClick={() => setShowOpportunityPopup(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {opportunitySources.map((source, index) => (
                <div key={index} className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-900 dark:text-white font-medium">
                        {source.metadata?.title || 'Unknown Opportunity'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {source.content.substring(0, 40)}
                        {source.content.length > 40 ? '...' : ''}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        handleOpportunityClick(source.metadata.entity_id);
                        setShowOpportunityPopup(false);
                      }}
                      className="ml-2 px-2 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded transition-colors duration-200 cursor-pointer"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Helper function to render attachment popup
  const renderAttachmentPopup = (contextSources) => {
    if (!contextSources || !Array.isArray(contextSources)) return null;
    
    const attachmentSources = contextSources.filter(source => 
      source.type === 'attachment' && source.metadata?.attachment_id
    );
    
    if (attachmentSources.length === 0) return null;
    
    const handleAttachmentClick = async (attachmentId, viewUrl) => {
      // Open attachment using authenticated fetch and blob URL
      try {
        const token = localStorage.getItem('token');
        const tenant = getProtectedTenantInfo();
        const tenantCode = tenant?.code;
        
        const response = await fetch(viewUrl || `/api/attachments/view/${attachmentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
          // Clean up blob URL after a delay
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        } else {
          console.error('Failed to load attachment:', response.status);
          // Fallback to direct URL
          window.open(viewUrl || `/api/attachments/view/${attachmentId}`, '_blank');
        }
      } catch (error) {
        console.error('Error opening attachment:', error);
        // Fallback to direct URL
        window.open(viewUrl || `/api/attachments/view/${attachmentId}`, '_blank');
      }
    };
    
    return (
      <div className="attachment-popup absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-64">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Related Attachments
            </span>
            <button
              onClick={() => setShowAttachmentPopup(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {attachmentSources.map((source, index) => {
            const metadata = source.metadata || {};
            const getFileIcon = () => {
              const mimeType = metadata.mime_type || '';
              if (mimeType.includes('pdf')) {
                return (
                  <svg className="h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                );
              } else if (mimeType.startsWith('image/')) {
                return (
                  <svg className="h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                );
              } else if (metadata.attachment_type === 'URL') {
                return (
                  <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                );
              }
              return (
                <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              );
            };

            const formatFileSize = (bytes) => {
              if (!bytes) return '';
              if (bytes < 1024) return bytes + ' B';
              if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
              return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
            };

            return (
              <div key={index} className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
                <div className="flex items-start space-x-2">
                  <div className="flex-shrink-0 mt-1">
                    {getFileIcon()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 dark:text-white font-medium truncate">
                      {metadata.title || metadata.file_name || 'Untitled Attachment'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {metadata.file_name && metadata.file_name !== metadata.title ? metadata.file_name : ''}
                    </div>
                    <div className="flex items-center space-x-2 mt-1 text-xs text-gray-400 dark:text-gray-500">
                      {metadata.file_size && (
                        <span>{formatFileSize(metadata.file_size)}</span>
                      )}
                      {metadata.attachment_type && (
                        <span className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                          {metadata.attachment_type}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      handleAttachmentClick(metadata.attachment_id, metadata.view_url);
                      setShowAttachmentPopup(false);
                    }}
                    className="ml-2 px-2 py-1 text-xs bg-pink-600 hover:bg-pink-700 dark:bg-pink-500 dark:hover:bg-pink-600 text-white rounded transition-colors duration-200 cursor-pointer flex-shrink-0"
                    title="Open attachment"
                  >
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Helper function to render all related links horizontally
  const renderRelatedLinks = (contextSources) => {
    if (!contextSources || !Array.isArray(contextSources)) return null;
    
    const salesNoteSources = contextSources.filter(source => 
      source.type === 'sales_note' && source.metadata?.entity_id
    );
    const customerSources = contextSources.filter(source => 
      source.type === 'customer' && source.metadata?.entity_id
    );
    const activitySources = contextSources.filter(source => 
      source.type === 'activity' && source.metadata?.entity_id
    );
    const leadSources = contextSources.filter(source => 
      source.type === 'lead' && source.metadata?.entity_id
    );
    const opportunitySources = contextSources.filter(source => 
      source.type === 'opportunity' && source.metadata?.entity_id
    );
    const attachmentSources = contextSources.filter(source => 
      source.type === 'attachment' && source.metadata?.attachment_id
    );
    
    // If no sources found, return null
    if (salesNoteSources.length === 0 && customerSources.length === 0 && 
        activitySources.length === 0 && leadSources.length === 0 && opportunitySources.length === 0 &&
        attachmentSources.length === 0) {
      return null;
    }
    
    return (
      <div className="mt-3 relative">
        <div className="flex flex-wrap gap-2">
          {salesNoteSources.length > 0 && (
            <button
              onClick={() => setShowSalesNotePopup(prev => !prev)}
              className="sales-note-popup inline-flex items-center px-2 py-1 text-xs bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white rounded transition-colors duration-200 cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Note
            </button>
          )}
          
          {customerSources.length > 0 && (
            <button
              onClick={() => setShowCustomerPopup(prev => !prev)}
              className="customer-popup inline-flex items-center px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded transition-colors duration-200 cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Customer
            </button>
          )}
          
          {activitySources.length > 0 && (
            <button
              onClick={() => setShowActivityPopup(prev => !prev)}
              className="activity-popup inline-flex items-center px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded transition-colors duration-200 cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Activity
            </button>
          )}
          
          {leadSources.length > 0 && (
            <button
              onClick={() => setShowLeadPopup(prev => !prev)}
              className="lead-popup inline-flex items-center px-2 py-1 text-xs bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white rounded transition-colors duration-200 cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Lead
            </button>
          )}
          
          {opportunitySources.length > 0 && (
            <button
              onClick={() => setShowOpportunityPopup(prev => !prev)}
              className="opportunity-popup inline-flex items-center px-2 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded transition-colors duration-200 cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              Opportunity
            </button>
          )}
          
          {attachmentSources.length > 0 && (
            <button
              onClick={() => setShowAttachmentPopup(prev => !prev)}
              className="attachment-popup inline-flex items-center px-2 py-1 text-xs bg-pink-600 hover:bg-pink-700 dark:bg-pink-500 dark:hover:bg-pink-600 text-white rounded transition-colors duration-200 cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              Attachment
            </button>
          )}
        </div>
        
        {/* Render all popups */}
        {showSalesNotePopup && renderSalesNotePopup(contextSources)}
        {showCustomerPopup && renderCustomerPopup(contextSources)}
        {showActivityPopup && renderActivityPopup(contextSources)}
        {showLeadPopup && renderLeadPopup(contextSources)}
        {showOpportunityPopup && renderOpportunityPopup(contextSources)}
        {showAttachmentPopup && renderAttachmentPopup(contextSources)}
      </div>
    );
  };

  // Helper function to render note popup
  const renderSalesNotePopup = (contextSources) => {
    if (!contextSources || !Array.isArray(contextSources)) return null;
    
    const salesNoteSources = contextSources.filter(source => 
      source.type === 'sales_note' && source.metadata?.entity_id
    );
    
    if (salesNoteSources.length === 0) return null;
    
    const handleSalesNoteClick = (entityId) => {
      navigate(`/notes?noteId=${entityId}`);
    };
    
    return (
      <div className="sales-note-popup absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-48">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Related Notes
            </span>
            <button
              onClick={() => setShowSalesNotePopup(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto">
          {salesNoteSources.map((source, index) => (
            <div key={index} className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 dark:text-white font-medium">
                    {source.metadata?.author_name || 'Unknown Author'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {source.content.substring(0, 40)}
                    {source.content.length > 40 ? '...' : ''}
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleSalesNoteClick(source.metadata.entity_id);
                    setShowSalesNotePopup(false);
                  }}
                  className="ml-2 px-2 py-1 text-xs bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white rounded transition-colors duration-200 cursor-pointer"
                >
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Helper function to render customer popup
  const renderCustomerPopup = (contextSources) => {
    if (!contextSources || !Array.isArray(contextSources)) return null;
    
    const customerSources = contextSources.filter(source => 
      source.type === 'customer' && source.metadata?.entity_id
    );
    
    if (customerSources.length === 0) return null;
    
    const handleCustomerClick = (entityId) => {
      navigate(`/customers?customerId=${entityId}`);
    };
    
    return (
      <div className="customer-popup absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-48">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Related Customers
            </span>
            <button
              onClick={() => setShowCustomerPopup(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto">
          {customerSources.map((source, index) => (
            <div key={index} className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 dark:text-white font-medium">
                    {source.metadata?.name || 'Unknown Customer'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {source.content.substring(0, 40)}
                    {source.content.length > 40 ? '...' : ''}
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleCustomerClick(source.metadata.entity_id);
                    setShowCustomerPopup(false);
                  }}
                  className="ml-2 px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded transition-colors duration-200 cursor-pointer"
                >
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Helper function to render activity popup
  const renderActivityPopup = (contextSources) => {
    if (!contextSources || !Array.isArray(contextSources)) return null;
    
    const activitySources = contextSources.filter(source => 
      source.type === 'activity' && source.metadata?.entity_id
    );
    
    if (activitySources.length === 0) return null;
    
    const handleActivityClick = (entityId) => {
      navigate(`/activities?activityId=${entityId}`);
    };
    
    return (
      <div className="activity-popup absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-48">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Related Activities
            </span>
            <button
              onClick={() => setShowActivityPopup(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto">
          {activitySources.map((source, index) => (
            <div key={index} className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 dark:text-white font-medium">
                    {source.metadata?.subject || 'Unknown Activity'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {source.content.substring(0, 40)}
                    {source.content.length > 40 ? '...' : ''}
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleActivityClick(source.metadata.entity_id);
                    setShowActivityPopup(false);
                  }}
                  className="ml-2 px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded transition-colors duration-200 cursor-pointer"
                >
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Helper function to render lead popup
  const renderLeadPopup = (contextSources) => {
    if (!contextSources || !Array.isArray(contextSources)) return null;
    
    const leadSources = contextSources.filter(source => 
      source.type === 'lead' && source.metadata?.entity_id
    );
    
    if (leadSources.length === 0) return null;
    
    const handleLeadClick = (entityId) => {
      navigate(`/leads?leadId=${entityId}`);
    };
    
    return (
      <div className="lead-popup absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-48">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Related Leads
            </span>
            <button
              onClick={() => setShowLeadPopup(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto">
          {leadSources.map((source, index) => (
            <div key={index} className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 dark:text-white font-medium">
                    {source.metadata?.contact_name || 'Unknown Lead'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {source.content.substring(0, 40)}
                    {source.content.length > 40 ? '...' : ''}
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleLeadClick(source.metadata.entity_id);
                    setShowLeadPopup(false);
                  }}
                  className="ml-2 px-2 py-1 text-xs bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white rounded transition-colors duration-200 cursor-pointer"
                >
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Helper function to render opportunity popup
  const renderOpportunityPopup = (contextSources) => {
    if (!contextSources || !Array.isArray(contextSources)) return null;
    
    const opportunitySources = contextSources.filter(source => 
      source.type === 'opportunity' && source.metadata?.entity_id
    );
    
    if (opportunitySources.length === 0) return null;
    
    const handleOpportunityClick = (entityId) => {
      navigate(`/opportunities?opportunityId=${entityId}`);
    };
    
    return (
      <div className="opportunity-popup absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 min-w-48">
        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Related Opportunities
            </span>
            <button
              onClick={() => setShowOpportunityPopup(false)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto">
          {opportunitySources.map((source, index) => (
            <div key={index} className="p-2 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 dark:text-white font-medium">
                    {source.metadata?.title || 'Unknown Opportunity'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {source.content.substring(0, 40)}
                    {source.content.length > 40 ? '...' : ''}
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleOpportunityClick(source.metadata.entity_id);
                    setShowOpportunityPopup(false);
                  }}
                  className="ml-2 px-2 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded transition-colors duration-200 cursor-pointer"
                >
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Helper function to get localized messages based on current language
  const getLocalizedMessages = () => {
    const isThaiLanguage = i18n.language === 'th' || i18n.language === 'th-TH';
    
    if (isThaiLanguage) {
      return {
        greeting: 'สวัสดี! ยินดีต้อนรับครับ 😊',
        helpMessage: 'วันนี้มีอะไรให้ช่วยไหมครับ?',
        inputPlaceholder: 'พิมพ์ข้อความของคุณที่นี่...',
        thinkingMessage: 'กำลังคิด...',
        suggestedQuestions: [
          'โอกาสที่ต้องติดตามมีอะไรบ้าง?',
          'ลูกค้าที่ใกล้จะปิดดีลมีใครบ้าง?',
          'แสดงโอกาสที่ค้างชำระเงิน',
          'ลูกค้าที่ไม่ได้ติดต่อนานที่สุดมีใครบ้าง?',
          'โอกาสที่เสี่ยงจะสูญหายมีอะไรบ้าง?',
          'สรุปยอดขายของเดือนนี้',
          'ลูกค้าที่มีโอกาสสูงสุดมีใครบ้าง?'
        ]
      };
    } else {
      return {
        greeting: 'Hi there! 👋',
        helpMessage: 'How can I help you today?',
        inputPlaceholder: 'Type your message here...',
        thinkingMessage: 'Thinking...',
        suggestedQuestions: [
          'What opportunities need follow-up?',
          'Which customers are close to closing?',
          'Show opportunities with pending payments',
          'Which customers haven\'t been contacted recently?',
          'What opportunities are at risk of being lost?',
          'Summarize this month\'s sales',
          'Which customers have the highest-value opportunities?'
        ]
      };
    }
  };

  // Handle clicking on a suggested question
  const handleSuggestedQuestion = async (question) => {
    if (isLoading) return;
    
    const userMessage = { role: 'user', content: question };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setIsLoading(true);

    try {
      const endpoint = ragEnabled ? '/api/ai/chat/rag' : '/api/ai/chat';
      const collectionName = getTenantCollectionName();
      const last10Messages = messages.slice(-10);
      const enhancedInput = enhanceQueryWithTemporalContext(question);
      const isDateQuery = isDateRelatedQuery(question);
      
      const requestBody = {
        message: enhancedInput,
        history: last10Messages,
        collection: collectionName,
      };

      if (ragEnabled) {
        requestBody.options = {
          agentType: 'crmAssistant',
          useSystemPrompt: true,
          channel: 'web',
          chatSource: 'salesbuddy',
          temporalContext: isDateQuery,
          strictRAG: strictRagMode, // Force RAG only, no greeting/count query bypasses
        };

        if (aiConfig?.prompts?.crmAssistant) {
          requestBody.options.temperature = aiConfig.prompts.crmAssistant.temperature;
          requestBody.options.max_tokens = aiConfig.prompts.crmAssistant.maxTokens;
          requestBody.options.systemPrompt = aiConfig.prompts.crmAssistant.systemPrompt;
        }
      }

      const response = await axios.post(endpoint, requestBody);
      const aiMessage = {
        role: 'assistant',
        content: response.data.reply,
        metadata: {
          ...response.data.metadata,
          context_sources: response.data.context_sources || null,
          mode: ragEnabled ? 'rag' : 'direct' // Track which mode was used
        },
        ragSources: ragEnabled ? response.data.metadata?.rag_context?.sources : null
      };
      
      setMessages(prevMessages => [...prevMessages, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: `Sorry, I'm having trouble connecting right now. Please try again in a moment! 🙏` 
      };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load tenant-specific AI configuration for assistant
  const loadAiConfiguration = async () => {
    try {
      setConfigLoading(true);
      const tenant = getProtectedTenantInfo();
      console.log('Loading AI config for tenant:', tenant);
      
      // Always set a default config first
      const defaultConfig = {
        models: {
          crmAssistant: { 
            service: 'openai', 
            model: 'gpt-3.5-turbo',
            enabled: true 
          }
        },
        prompts: {
          crmAssistant: {
            systemPrompt: 'You are a helpful AI assistant for customer support.',
            temperature: 0.8,
            maxTokens: 1500
          }
        },
        lineChannels: {
          powerAssistant: {
            enabled: false,
            channelSecret: null,
            qrCodeUrl: null,
            lineId: null,
            userAccessToken: null
          }
        }
      };
      
      setAiConfig(defaultConfig);

      if (!tenant || tenant.code === 'DEFAULT') {
        console.log('No valid tenant found, using default config');
        return;
      }

      // Try to load tenant-specific config
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No auth token found, using default config');
        return;
      }

      // Load both AI config and Line config in parallel
      const [aiConfigResponse, lineConfigResponse] = await Promise.all([
        axios.get(`/api/ai-config/tenant/${tenant.code}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }),
        axios.get(`/api/line-config/tenant/${tenant.code}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }).catch(() => ({ data: null })) // Gracefully handle if line-config doesn't exist yet
      ]);

      if (aiConfigResponse.data) {
        console.log('AI config loaded successfully:', aiConfigResponse.data);
        setAiConfig(aiConfigResponse.data);
      }
      
      // Load Line configuration from new line_config.json
      if (lineConfigResponse?.data?.channels?.SalesBuddy) {
        const salesBuddyConfig = lineConfigResponse.data.channels.SalesBuddy;
        // Update aiConfig with Line config for backward compatibility in UI
        setAiConfig(prev => ({
          ...prev,
          lineChannels: {
            ...prev?.lineChannels,
            salesBuddy: salesBuddyConfig,
            powerAssistant: salesBuddyConfig // Keep old key for backward compatibility
          }
        }));
        
        // If Line is configured, fetch profile information
        if (salesBuddyConfig?.enabled && salesBuddyConfig?.userAccessToken) {
          loadLineProfile(salesBuddyConfig.userAccessToken);
        }
      } else if (aiConfigResponse.data?.lineChannels?.powerAssistant) {
        // Fallback to old ai_config.json structure if line_config.json doesn't exist
        const powerAssistantConfig = aiConfigResponse.data.lineChannels.powerAssistant;
        if (powerAssistantConfig?.enabled && powerAssistantConfig?.userAccessToken) {
          loadLineProfile(powerAssistantConfig.userAccessToken);
        }
      }
    } catch (error) {
      console.warn('Failed to load AI configuration for chatbox (using defaults):', error.response?.status, error.response?.statusText);
      
      // Keep the default config that was already set
      // Don't throw error, just log it and continue with defaults
      if (error.response?.status === 403) {
        console.warn('Access denied to AI config - user may not have manager/admin permissions');
      } else if (error.response?.status === 401) {
        console.warn('Authentication failed - token may be expired');
      }
    } finally {
      setConfigLoading(false);
    }
  };

  // Load Line profile information
  const loadLineProfile = async (accessToken) => {
    try {
      setLineProfileLoading(true);
      
      // Fetch profile from Line API
      const response = await axios.get('https://api.line.me/v2/profile', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.data) {
        setLineProfile(response.data);
        console.log('✅ Line profile loaded for SalesBuddy:', response.data);
      }
    } catch (error) {
      console.error('Failed to load Line profile for SalesBuddy:', error);
      // If we can't get profile, we'll still show the Line icon
      setLineProfile(null);
    } finally {
      setLineProfileLoading(false);
    }
  };

  useEffect(() => {
    loadAiConfiguration();
  }, []);

  // Watch for AI configuration changes and refresh Line profile if needed
  useEffect(() => {
    const salesBuddyConfig = aiConfig?.lineChannels?.salesBuddy || aiConfig?.lineChannels?.powerAssistant;
    if (salesBuddyConfig?.enabled && 
        salesBuddyConfig?.userAccessToken &&
        salesBuddyConfig?.userAccessToken !== lineProfile?.accessToken) {
      loadLineProfile(salesBuddyConfig.userAccessToken);
    }
  }, [aiConfig?.lineChannels?.salesBuddy?.userAccessToken, aiConfig?.lineChannels?.powerAssistant?.userAccessToken]);

  useEffect(() => {
    if (chatboxMessagesRef.current) {
      chatboxMessagesRef.current.scrollTop = chatboxMessagesRef.current.scrollHeight;
    }
  }, [messages]);

  // Save position preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sales-buddy-position', JSON.stringify(position));
    // Keep legacy format for backward compatibility
    localStorage.setItem('sales-buddy-side', JSON.stringify(position === 'right'));
  }, [position]);

  // Save minimized state to localStorage
  useEffect(() => {
    localStorage.setItem('sales-buddy-minimized', JSON.stringify(isMinimized));
  }, [isMinimized]);

  // Save chatbox width to localStorage
  useEffect(() => {
    localStorage.setItem('sales-buddy-width', chatboxWidth.toString());
  }, [chatboxWidth]);

  // Detect mobile on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Resize drag handlers for desktop
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingRef.current || isMobile || !isExpanded) return;
      
      // Only allow resizing when on left or right side
      if (position !== 'left' && position !== 'right') return;
      
      const newWidth = position === 'right'
        ? window.innerWidth - e.clientX 
        : e.clientX;
      
      // Set min and max width constraints
      const minWidth = 320;
      const maxWidth = window.innerWidth * 0.8;
      const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
      
      setChatboxWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false;
        setIsResizing(false);
        document.body.style.cursor = 'default';
        document.body.style.userSelect = 'auto';
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [position, isMobile, isExpanded]);

  // Start resize drag
  const handleResizeStart = (e) => {
    e.preventDefault();
    isResizingRef.current = true;
    setIsResizing(true);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  };

  // Navigate to different edge positions
  const navigateToEdge = (newPosition) => {
    if (['top', 'bottom', 'left', 'right'].includes(newPosition)) {
      setPosition(newPosition);
    }
  };

  // Toggle side function (backward compatibility - toggles between left/right)
  const toggleSide = () => {
    setPosition(position === 'right' ? 'left' : 'right');
  };
  
  // Get available navigation options from current position
  const getAvailablePositions = () => {
    const allPositions = ['top', 'bottom', 'left', 'right'];
    return allPositions.filter(pos => pos !== position);
  };
  
  // Helper function to render arrow icon
  const renderArrowIcon = (direction) => {
    const arrowPaths = {
      up: 'M5 15l7-7 7 7',
      down: 'M19 9l-7 7-7-7',
      left: 'M15 19l-7-7 7-7',
      right: 'M9 5l7 7-7 7'
    };
    
    return (
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d={arrowPaths[direction] || arrowPaths.right} />
      </svg>
    );
  };

  // Toggle minimized/ribbon state
  const toggleMinimized = () => {
    setIsMinimized(!isMinimized);
  };

  // Restore from ribbon (show balloon)
  const restoreFromRibbon = () => {
    setIsMinimized(false);
  };

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSalesNotePopup && !event.target.closest('.sales-note-popup')) {
        setShowSalesNotePopup(false);
      }
      if (showCustomerPopup && !event.target.closest('.customer-popup')) {
        setShowCustomerPopup(false);
      }
      if (showActivityPopup && !event.target.closest('.activity-popup')) {
        setShowActivityPopup(false);
      }
      if (showLeadPopup && !event.target.closest('.lead-popup')) {
        setShowLeadPopup(false);
      }
      if (showOpportunityPopup && !event.target.closest('.opportunity-popup')) {
        setShowOpportunityPopup(false);
      }
      if (showAttachmentPopup && !event.target.closest('.attachment-popup')) {
        setShowAttachmentPopup(false);
      }
      // Close question panel when clicking outside (but not on the ribbon itself)
      if (showQuestionPanel && !event.target.closest('.question-panel') && !event.target.closest('.question-ribbon')) {
        setShowQuestionPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSalesNotePopup, showCustomerPopup, showActivityPopup, showLeadPopup, showOpportunityPopup, showAttachmentPopup, showQuestionPanel]);

  const toggleChatbox = () => {
    setIsOpen(!isOpen);
    // Reset expanded state when closing
    if (isOpen) {
      setIsExpanded(false);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleSend = async () => {
    if (input.trim() === '') return;

    const userMessage = { role: 'user', content: input };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    const currentInput = input;
    setInput('');
    setIsLoading(true);

    try {
      // Generate task session ID if not exists (for sequential action tracking)
      if (!taskSessionId) {
        const newSessionId = `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        setTaskSessionId(newSessionId);
        console.log('📝 Generated new task session ID:', newSessionId);
      }

      console.log('🔍 RAG Status:', { ragEnabled, endpoint: ragEnabled ? '/api/ai/chat/rag' : '/api/ai/chat' });
      console.log('🔍 Task Session ID:', taskSessionId || 'will generate new one');
      const endpoint = ragEnabled ? '/api/ai/chat/rag' : '/api/ai/chat';
      const collectionName = getTenantCollectionName();
      console.log('🔍 Collection Name:', collectionName);
      console.log('🔍 Tenant Info:', getProtectedTenantInfo());
      
      // Get last 10 messages for continuous conversation context
      const last10Messages = messages.slice(-10);
      
      // Enhance query with temporal context if it's date-related
      const enhancedInput = enhanceQueryWithTemporalContext(currentInput);
      const isDateQuery = isDateRelatedQuery(currentInput);
      
      console.log('🔍 Query analysis:', {
        original: currentInput,
        enhanced: enhancedInput,
        isDateQuery: isDateQuery,
        hasTemporalContext: enhancedInput !== currentInput,
        temporalKeywords: extractTemporalKeywords(currentInput)
      });
      
      const requestBody = {
        message: enhancedInput,
        history: last10Messages,
        collection: collectionName, // Always include collection
      };

      // Add AI config for RAG requests
      if (ragEnabled) {
        console.log('🔍 Setting collection in request:', collectionName);
        console.log('🔒 Strict RAG Mode:', strictRagMode);
        requestBody.options = {
          agentType: 'crmAssistant', // Use crmAssistant config on backend
          useSystemPrompt: true,
          channel: 'web', // Explicitly tag channel for chat history
          chatSource: 'salesbuddy', // Tag source for admin explorer filters
          temporalContext: isDateQuery, // Flag for temporal context processing
          taskSessionId: taskSessionId, // Pass task session ID for sequential action tracking
          strictRAG: strictRagMode, // Force RAG only, no greeting/count query bypasses
          // Note: Intent analysis is now handled by the backend RAG service
        };

        // If we have loaded AI config, include system prompt, temperature and max tokens
        if (aiConfig?.prompts?.crmAssistant) {
          requestBody.options.temperature = aiConfig.prompts.crmAssistant.temperature;
          requestBody.options.max_tokens = aiConfig.prompts.crmAssistant.maxTokens;
          requestBody.options.systemPrompt = aiConfig.prompts.crmAssistant.systemPrompt;
        }
      }

      console.log('🔍 Sending request:', {
        endpoint,
        ragEnabled,
        requestBody: {
          message: requestBody.message,
          historyLength: requestBody.history?.length || 0,
          collection: requestBody.collection,
          options: requestBody.options
        }
      });
      console.log('🔍 Full request body:', JSON.stringify(requestBody, null, 2));

      const response = await axios.post(endpoint, requestBody);

      console.log('🔍 RAG response received:', {
        hasReply: !!response.data.reply,
        replyLength: response.data.reply?.length || 0,
        hasMetadata: !!response.data.metadata,
        metadata: response.data.metadata,
        hasContextSources: !!response.data.context_sources,
        contextSourcesCount: response.data.context_sources?.length || 0,
        mode: response.data.metadata?.mode,
        requiresApproval: response.data.metadata?.requiresApproval,
        proposedActions: response.data.proposedActions?.length || 0
      });
      
      // 🔍 DEBUG: Detailed response structure logging
      console.log('🔍 [FRONTEND] Detailed response structure:');
      console.log('   response.data.metadata?.mode:', response.data.metadata?.mode);
      console.log('   response.data.proposedActions:', response.data.proposedActions);
      console.log('   response.data.proposedActions?.length:', response.data.proposedActions?.length);
      console.log('   response.data.metadata?.proposedActions:', response.data.metadata?.proposedActions);
      console.log('   response.data.metadata?.proposedActions?.length:', response.data.metadata?.proposedActions?.length);
      console.log('   response.data.thinkingProcess:', response.data.thinkingProcess ? 'exists' : 'missing');
      console.log('   response.data.reasoning:', response.data.reasoning ? 'exists' : 'missing');
      console.log('   Condition check:');
      console.log('     - metadata.mode === "planning":', response.data.metadata?.mode === 'planning');
      console.log('     - proposedActions?.length > 0:', (response.data.proposedActions?.length || 0) > 0);
      console.log('     - metadata.proposedActions?.length > 0:', (response.data.metadata?.proposedActions?.length || 0) > 0);
      console.log('     - Combined condition:', response.data.metadata?.mode === 'planning' && (response.data.proposedActions?.length > 0 || response.data.metadata?.proposedActions?.length > 0));

      // Check if this is a planning response with actions requiring approval
      // Check both top-level and metadata-level proposedActions
      const hasProposedActions = (response.data.proposedActions?.length > 0) || (response.data.metadata?.proposedActions?.length > 0);
      const hasDirectActions = (response.data.directActions?.length > 0) || (response.data.metadata?.directActions?.length > 0);
      const isPlanningMode = response.data.metadata?.mode === 'planning';
      
      // Get directActions that were executed automatically
      const directActions = response.data.directActions || response.data.metadata?.directActions || [];
      
      if (isPlanningMode && hasProposedActions) {
        // Show thinking process and action approval UI
        // Use proposedActions from top-level or metadata
        const proposedActions = response.data.proposedActions || response.data.metadata?.proposedActions || [];
        console.log('✅ [FRONTEND] Planning mode detected - showing Action Approval UI');
        console.log('   Proposed Actions Count:', proposedActions.length);
        console.log('   Direct Actions Count:', directActions.length);
        console.log('   Proposed Actions:', proposedActions);
        console.log('   Direct Actions:', directActions);
        
        const aiMessage = {
          role: 'assistant',
          content: response.data.reply || response.data.summary,
          thinkingProcess: response.data.thinkingProcess,
          reasoning: response.data.reasoning,
          proposedActions: proposedActions,
          directActions: directActions, // Include directActions that were executed
          todoList: response.data.todoList || [], // Todo list with all actions
          metadata: {
            ...response.data.metadata,
            context_sources: response.data.context_sources || null,
            requiresApproval: true,
            mode: ragEnabled ? 'rag' : 'direct' // Track which mode was used
          },
          ragSources: null
        };
        
        setMessages(prevMessages => {
          const newMessages = [...prevMessages, aiMessage];
          setPendingActions({
            messageIndex: newMessages.length - 1, // Index of the AI message we just added
            actions: proposedActions, // Only actions requiring approval
            originalMessage: currentInput
          });
          return newMessages;
        });
      } else {
        // Regular response (no actions requiring approval, but may have directActions)
        const aiMessage = {
          role: 'assistant',
          content: response.data.reply,
          directActions: directActions, // Include directActions that were executed automatically
          todoList: response.data.todoList || [], // Todo list with all actions
          metadata: {
            ...response.data.metadata,
            context_sources: response.data.context_sources || null,
            mode: ragEnabled ? 'rag' : 'direct' // Track which mode was used
          },
          ragSources: ragEnabled ? response.data.metadata?.rag_context?.sources : null
        };
        
        setMessages(prevMessages => [...prevMessages, aiMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: `Sorry, I'm having trouble connecting right now. Please try again in a moment! 🙏` 
      };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRAG = () => {
    setRagEnabled(!ragEnabled);
  };

  // Copy assistant message content to clipboard (plain text)
  const handleCopyMessage = async (index, content) => {
    try {
      // Simply copy the plain text content
      await navigator.clipboard.writeText(content || '');
      setCopiedMessageIndex(index);
      setTimeout(() => setCopiedMessageIndex(null), 1200);
    } catch (e) {
      console.warn('Failed to copy message:', e);
    }
  };

  console.log('🔍 SalesBuddy render state:', { isOpen, position, aiConfig: !!aiConfig });

  // Render ribbon when minimized
  if (!isOpen && isMinimized) {
    console.log('🔍 Rendering ribbon on:', position);
    const isVertical = position === 'left' || position === 'right';
    const isHorizontal = position === 'top' || position === 'bottom';
    const availablePositions = getAvailablePositions();
    
    // Calculate ribbon styles based on position
    const getRibbonStyles = () => {
      const baseStyle = {
        zIndex: 9999,
      };
      
      if (position === 'left') {
        return {
          ...baseStyle,
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: '40px',
          height: isMobile ? '100px' : '220px', // Increased to fit icon, label, and arrows
          borderTopRightRadius: '12px',
          borderBottomRightRadius: '12px',
        };
      } else if (position === 'right') {
        return {
          ...baseStyle,
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: '40px',
          height: isMobile ? '100px' : '220px', // Increased to fit icon, label, and arrows
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
        };
      } else if (position === 'top') {
        return {
          ...baseStyle,
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: isMobile ? '120px' : '200px',
          height: '40px',
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
        };
      } else { // bottom
        return {
          ...baseStyle,
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: isMobile ? '120px' : '200px',
          height: '40px',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
        };
      }
    };

    const ribbonStyles = getRibbonStyles();
    const gradientClass = isVertical 
      ? 'bg-gradient-to-b from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700'
      : 'bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700';

    return (
      <div
        className={`fixed ${gradientClass} text-white shadow-2xl hover:shadow-teal-500/50 transition-all duration-300 group`}
        style={ribbonStyles}
        title="Click to show SalesBuddy"
      >
        {isVertical ? (
          // Vertical ribbon (left/right)
          <div className="flex flex-col items-center h-full py-4 relative">
            {/* Click area to restore */}
            <div 
              onClick={restoreFromRibbon}
              className="absolute inset-0 cursor-pointer"
            />
            
            {/* Icon at top */}
            <div className="relative h-6 w-6 transform group-hover:scale-110 transition-transform duration-200 flex-shrink-0 z-10 pointer-events-none mb-3">
              <User className="h-6 w-6" />
              <Search className="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-yellow-400 dark:bg-yellow-500 rounded-full p-0.5" />
            </div>
            
            {/* Text in middle - visible only on desktop */}
            <span className="hidden md:block text-[10px] font-bold tracking-widest transform rotate-90 origin-center whitespace-nowrap flex-shrink-0 my-4 z-10 pointer-events-none">
              SALESBUDDY
            </span>
            
            {/* Navigation arrows at bottom */}
            <div className="flex flex-col items-center space-y-1.5 mt-auto z-20" onClick={(e) => e.stopPropagation()}>
              {availablePositions.map((targetPos) => {
                // Map target position to arrow direction
                const getArrowDirection = () => {
                  if (targetPos === 'top') return 'up';
                  if (targetPos === 'bottom') return 'down';
                  if (targetPos === 'left') return 'left';
                  if (targetPos === 'right') return 'right';
                  return 'right';
                };
                
                return (
                  <button
                    key={targetPos}
                    onClick={() => navigateToEdge(targetPos)}
                    className="w-5 h-5 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                    title={`Move to ${targetPos}`}
                  >
                    {renderArrowIcon(getArrowDirection())}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          // Horizontal ribbon (top/bottom)
          <div className="flex items-center justify-between h-full px-3 relative">
            {/* Click area to restore */}
            <div 
              onClick={restoreFromRibbon}
              className="absolute inset-0 cursor-pointer"
            />
            
            {/* Navigation arrows on left */}
            <div className="flex items-center space-x-1 z-20" onClick={(e) => e.stopPropagation()}>
              {availablePositions.map((targetPos) => {
                // Map target position to arrow direction
                const getArrowDirection = () => {
                  if (targetPos === 'top') return 'up';
                  if (targetPos === 'bottom') return 'down';
                  if (targetPos === 'left') return 'left';
                  if (targetPos === 'right') return 'right';
                  return 'right';
                };
                
                return (
                  <button
                    key={targetPos}
                    onClick={() => navigateToEdge(targetPos)}
                    className="w-5 h-5 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                    title={`Move to ${targetPos}`}
                  >
                    {renderArrowIcon(getArrowDirection())}
                  </button>
                );
              })}
            </div>
            
            {/* Icon in center */}
            <div className="relative h-6 w-6 transform group-hover:scale-110 transition-transform duration-200 flex-shrink-0 z-10 pointer-events-none">
              <User className="h-6 w-6" />
              <Search className="absolute -bottom-0.5 -right-0.5 h-3 w-3 bg-yellow-400 dark:bg-yellow-500 rounded-full p-0.5" />
            </div>
            
            {/* Text - visible only on desktop */}
            <span className="hidden md:block text-[10px] font-bold tracking-widest whitespace-nowrap flex-shrink-0 z-10 pointer-events-none">
              SALESBUDDY
            </span>
          </div>
        )}
      </div>
    );
  }

  if (!isOpen) {
    console.log('🔍 Rendering floating button on:', position);
    
    // Calculate position for floating button based on edge
    const getButtonPosition = () => {
      switch (position) {
        case 'left':
          return { bottom: '24px', left: '24px' };
        case 'right':
          return { bottom: '24px', right: '24px' };
        case 'top':
          return { top: '24px', left: '50%', transform: 'translateX(-50%)' };
        case 'bottom':
          return { bottom: '24px', left: '50%', transform: 'translateX(-50%)' };
        default:
          return { bottom: '24px', right: '24px' };
      }
    };
    
    const buttonPos = getButtonPosition();
    const availablePositions = getAvailablePositions();
    
    return (
      <div className="fixed" style={{ 
        ...buttonPos,
        zIndex: 9999 
      }}>
        {/* Main SalesBuddy Button */}
        <button
          onClick={toggleChatbox}
          className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-full shadow-xl hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 transform hover:scale-105 flex flex-col items-center justify-center cursor-pointer"
          style={{
            width: '96px',
            height: '96px',
            display: 'flex'
          }}
          aria-label="Open SalesBuddy"
          title="Click to open SalesBuddy"
        >
          <div className="text-center">
            <div className="mb-1 flex justify-center">
              <div className="relative h-8 w-8">
                <User className="h-8 w-8" />
                <Search className="absolute -bottom-1 -right-1 h-4 w-4 bg-yellow-400 dark:bg-yellow-500 rounded-full p-0.5" />
              </div>
            </div>
            <div className="text-xs font-semibold leading-tight">
              Sales<br/>Buddy
            </div>
          </div>
        </button>
        
        {/* Hide/Minimize Button */}
        <button
          onClick={toggleMinimized}
          className="absolute bg-orange-500 dark:bg-orange-600 text-white hover:bg-orange-600 dark:hover:bg-orange-700 rounded-full shadow-lg transition-all duration-200 flex items-center justify-center hover:scale-110"
          style={{
            width: '24px',
            height: '24px',
            ...(position === 'top' ? { top: '-8px', left: '50%', transform: 'translateX(-50%)' } :
                position === 'bottom' ? { bottom: '-8px', left: '50%', transform: 'translateX(-50%)' } :
                position === 'right' ? { top: '-8px', right: '-8px' } :
                { top: '-8px', left: '-8px' })
          }}
          aria-label="Minimize to ribbon"
          title="Minimize to ribbon"
        >
          {position === 'top' ? (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 14l-7-7m0 0l-7 7m7-7v18" />
            </svg>
          ) : position === 'bottom' ? (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          ) : position === 'right' ? (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          ) : (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          )}
        </button>
        
        {/* Navigation Buttons to Switch Edges */}
        <div className="absolute flex gap-1"
          style={{
            ...(position === 'top' ? { bottom: '-32px', left: '50%', transform: 'translateX(-50%)', flexDirection: 'row' } :
                position === 'bottom' ? { top: '-32px', left: '50%', transform: 'translateX(-50%)', flexDirection: 'row' } :
                position === 'right' ? { left: '-32px', top: '50%', transform: 'translateY(-50%)', flexDirection: 'column' } :
                { right: '-32px', top: '50%', transform: 'translateY(-50%)', flexDirection: 'column' })
          }}
        >
          {availablePositions.slice(0, 3).map((targetPos) => {
            // Map target position to arrow direction
            const getArrowDirection = () => {
              if (targetPos === 'top') return 'up';
              if (targetPos === 'bottom') return 'down';
              if (targetPos === 'left') return 'left';
              if (targetPos === 'right') return 'right';
              return 'right';
            };
            
            return (
              <button
                key={targetPos}
                onClick={() => navigateToEdge(targetPos)}
                className="bg-gradient-to-r from-blue-500 to-indigo-500 dark:from-blue-600 dark:to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-600 dark:hover:from-blue-700 dark:hover:to-indigo-700 rounded-full shadow-lg transition-all duration-200 flex items-center justify-center hover:scale-110"
                style={{
                  width: '24px',
                  height: '24px'
                }}
                aria-label={`Move to ${targetPos}`}
                title={`Move to ${targetPos}`}
              >
                {renderArrowIcon(getArrowDirection())}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  console.log('💬 Rendering opened chatbox on:', position);
  
  // Calculate chatbox position based on edge
  const getChatboxPosition = () => {
    if (isExpanded) {
      // Expanded mode - only work on left/right edges
      if (position === 'left' || position === 'right') {
        return {
          top: 0,
          [position]: 0,
          width: isMobile ? '100%' : `${chatboxWidth}px`,
          height: '100%',
          borderRadius: 0
        };
      } else {
        // If on top/bottom, move to nearest corner for expansion
        return {
          top: 0,
          right: 0,
          width: isMobile ? '100%' : `${chatboxWidth}px`,
          height: '100%',
          borderRadius: 0
        };
      }
    } else {
      // Collapsed mode
      switch (position) {
        case 'left':
          return {
            bottom: 16,
            left: 16,
            width: isMobile ? 'calc(100% - 32px)' : 384,
            maxWidth: isMobile ? '100%' : 'none',
            maxHeight: 600,
            borderRadius: '0.5rem'
          };
        case 'right':
          return {
            bottom: 16,
            right: 16,
            width: isMobile ? 'calc(100% - 32px)' : 384,
            maxWidth: isMobile ? '100%' : 'none',
            maxHeight: 600,
            borderRadius: '0.5rem'
          };
        case 'top':
          return {
            top: '5.5rem', // Below Layout header (h-14/h-16) so chatbox is not hidden
            left: '50%',
            transform: 'translateX(-50%)',
            width: isMobile ? 'calc(100% - 32px)' : 384,
            maxWidth: isMobile ? '100%' : 'none',
            maxHeight: 600,
            borderRadius: '0.5rem'
          };
        case 'bottom':
          return {
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            width: isMobile ? 'calc(100% - 32px)' : 384,
            maxWidth: isMobile ? '100%' : 'none',
            maxHeight: 600,
            borderRadius: '0.5rem'
          };
        default:
          return {
            bottom: 16,
            right: 16,
            width: isMobile ? 'calc(100% - 32px)' : 384,
            maxWidth: isMobile ? '100%' : 'none',
            maxHeight: 600,
            borderRadius: '0.5rem'
          };
      }
    }
  };
  
  return (
    <div
      ref={dragRef}
      className="fixed bg-cyan-50 dark:bg-gray-900/95 rounded-lg shadow-xl border border-cyan-200 dark:border-cyan-800 flex flex-col z-[9999]"
      style={{
        transition: isResizing ? 'none' : 'all 0.3s',
        ...getChatboxPosition()
      }}
    >
      {/* Resize Handle for Desktop Expanded Mode - only on left/right */}
      {isExpanded && !isMobile && (position === 'left' || position === 'right') && (
        <div
          ref={resizeRef}
          onMouseDown={handleResizeStart}
          className="absolute top-0 bottom-0 w-1 hover:w-2 bg-cyan-300/30 dark:bg-cyan-500/30 hover:bg-cyan-500 dark:hover:bg-cyan-400 cursor-ew-resize transition-all z-50 group"
          style={{
            [position === 'right' ? 'left' : 'right']: 0
          }}
          title="Drag to resize"
        >
          {/* Visual grip indicator */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex flex-col space-y-1">
              <div className="w-0.5 h-8 bg-white rounded-full"></div>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div 
        className="p-4 bg-gradient-to-r from-cyan-600 to-teal-600 dark:from-cyan-700 dark:to-teal-700 text-white rounded-t-lg flex justify-between items-center flex-shrink-0"
      >
        <div className="flex items-center space-x-2">
          <h3 className="font-bold text-lg">SalesBuddy</h3>
          {/* Line Icon */}
          <button
            onClick={() => setShowLineQrModal(true)}
            className={`p-1 rounded transition-colors hover:scale-110 ${
              (aiConfig?.lineChannels?.salesBuddy || aiConfig?.lineChannels?.powerAssistant)?.channelSecret 
                ? 'bg-green-500 dark:bg-green-600 text-white hover:bg-green-600 dark:hover:bg-green-700' 
                : 'bg-gray-400 dark:bg-gray-500 text-gray-200 dark:text-gray-300 hover:bg-gray-500 dark:hover:bg-gray-600'
            }`}
            title={
              (aiConfig?.lineChannels?.salesBuddy || aiConfig?.lineChannels?.powerAssistant)?.channelSecret 
                ? "Click to view Line QR Code" 
                : "Line SalesBuddy is not configured"
            }
          >
            {lineProfile?.pictureUrl ? (
              // Show Line profile picture when available
              <img
                src={lineProfile.pictureUrl}
                alt="Line Profile"
                className="w-4 h-4 rounded-full object-cover"
              />
            ) : (
              // Show Line icon when profile is not available
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
              </svg>
            )}
          </button>

        </div>
        <div className="flex items-center space-x-2">
          {/* Position Navigation Button */}
          <div className="relative group">
            <button 
              className="p-1.5 rounded-md bg-white/20 hover:bg-white/30 text-white transition-all duration-200 hover:scale-110"
              aria-label="Move to different edge"
              title="Move to different edge"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            
            {/* Dropdown menu with all positions */}
            <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="p-1">
                {getAvailablePositions().map((targetPos) => (
                  <button
                    key={targetPos}
                    onClick={() => navigateToEdge(targetPos)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-700 rounded flex items-center justify-between"
                  >
                    <span className="capitalize">{targetPos}</span>
                    <span className="text-xs">
                      {targetPos === 'top' ? '↑' : targetPos === 'bottom' ? '↓' : targetPos === 'left' ? '←' : '→'}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          {/* RAG/Direct Mode Toggle Button */}
          <button 
            onClick={toggleRAG}
            className={`p-1.5 rounded-md transition-all duration-200 hover:scale-110 ${
              ragEnabled 
                ? 'bg-gradient-to-r from-teal-400 to-cyan-400 text-white' 
                : 'bg-white/20 hover:bg-white/30 text-white'
            }`}
            aria-label={ragEnabled ? i18n.t('salesBuddy.chatMode.rag') : i18n.t('salesBuddy.chatMode.direct')}
            title={ragEnabled ? i18n.t('salesBuddy.chatMode.ragTooltip') : i18n.t('salesBuddy.chatMode.directTooltip')}
          >
            {ragEnabled ? (
              // RAG Mode - Brain icon
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            ) : (
              // Direct Mode - Simple chat icon
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            )}
          </button>
          {/* Strict RAG Toggle (only show when RAG is enabled) */}
          {ragEnabled && (
            <button 
              onClick={() => {
                const newValue = !strictRagMode;
                console.log('🔒 Toggling Strict RAG Mode:', strictRagMode, '->', newValue);
                setStrictRagMode(newValue);
              }}
              className={`p-1.5 rounded-md transition-all duration-200 hover:scale-110 text-xs font-medium ${
                strictRagMode 
                  ? 'bg-orange-500 text-white shadow-lg ring-2 ring-orange-300' 
                  : 'bg-white/20 hover:bg-white/30 text-white'
              }`}
              aria-label={i18n.t('salesBuddy.chatMode.strictRag')}
              title={i18n.t('salesBuddy.chatMode.strictRagTooltip')}
            >
              {strictRagMode ? (
                // Locked icon (strict mode ON)
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              ) : (
                // Unlocked icon (strict mode OFF)
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          )}
          {/* Expand/Collapse Button */}
          <button 
            onClick={toggleExpanded} 
            className="text-white hover:text-gray-200 dark:hover:text-gray-300 transition-colors"
            aria-label={isExpanded ? "Collapse chat" : "Expand chat"}
            title={isExpanded ? "Collapse chat" : "Expand chat"}
          >
            {isExpanded ? (
              // Collapse icon (minimize)
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7-7m0 0l-7 7m7-7v18" />
              </svg>
            ) : (
              // Expand icon (maximize)
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            )}
          </button>
          {/* Close Button */}
          <button 
            onClick={toggleChatbox} 
            className="text-white hover:text-gray-200 dark:hover:text-gray-300 transition-colors"
            aria-label="Close chat"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Question Ribbon - Below Header */}
      <div 
        className="question-ribbon bg-gradient-to-r from-teal-500 to-cyan-500 dark:from-teal-600 dark:to-cyan-600 flex items-center justify-center cursor-pointer hover:from-teal-600 hover:to-cyan-600 dark:hover:from-teal-700 dark:hover:to-cyan-700 transition-all duration-200 py-2 border-b border-teal-400 dark:border-teal-500"
        onClick={() => setShowQuestionPanel(!showQuestionPanel)}
        title={showQuestionPanel ? "Hide questions" : "Show suggested questions"}
      >
        <div className="flex items-center space-x-2 text-white text-xs font-medium">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>💡 Suggested Questions</span>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className={`h-3 w-3 transition-transform duration-200 ${showQuestionPanel ? 'rotate-180' : ''}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Question Panel - Slide Down */}
      <div 
        className={`question-panel overflow-hidden transition-all duration-300 ease-in-out ${
          showQuestionPanel ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-lg">
          <div className="space-y-3">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Click any question to ask:
            </p>
            <div className="grid grid-cols-1 gap-2 max-h-80 overflow-y-auto">
              {getLocalizedMessages().suggestedQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => {
                    handleSuggestedQuestion(question);
                    setShowQuestionPanel(false);
                  }}
                  disabled={isLoading}
                  className="w-full text-left px-4 py-3 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-600 hover:border-blue-300 dark:hover:border-blue-500 text-gray-700 dark:text-gray-300 hover:text-blue-700 dark:hover:text-blue-300 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-start space-x-2 group"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mt-0.5 text-gray-400 group-hover:text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="flex-1">{question}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div 
        ref={chatboxMessagesRef} 
        className={`p-4 overflow-y-auto flex-grow ${
          isExpanded ? 'h-full' : 'h-80'
        }`}
      >
        {messages.length === 0 && (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8 px-4">
            <div className="mb-2 text-4xl">👋</div>
            <p className="text-lg font-medium mb-2">{getLocalizedMessages().greeting}</p>
            <p className="text-sm">
              {getLocalizedMessages().helpMessage}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
              💡 Click "Suggested Questions" above to see helpful prompts
            </p>
          </div>
        )}
        {messages.map((msg, index) => {
          // Check if this is an assistant message with only result data (no text needed)
          const hasCountResult = msg.directActions?.some(a => 
            a.executed && a.name === 'count_entities' && (a.result?.result || a.result?.count !== undefined)
          );
          const hasEntityDataResult = msg.directActions?.some(a => 
            a.executed && a.name === 'get_entity_data' && a.result?.result
          );
          const hasExecutionCountResult = msg.metadata?.executionResults?.some(r => 
            r.success && r.name === 'count_entities' && (r.result?.count !== undefined || r.result?.result?.count !== undefined)
          );
          const hasExecutionEntityDataResult = msg.metadata?.executionResults?.some(r => 
            r.success && r.name === 'get_entity_data' && (r.result?.data !== undefined || r.result?.result?.data !== undefined)
          );
          
          const shouldHideMessageBox = msg.role === 'assistant' && (hasCountResult || hasEntityDataResult || hasExecutionCountResult || hasExecutionEntityDataResult);

          return (
            <div key={index} className="my-2">
              {!shouldHideMessageBox && (
                <div 
                  className={`p-3 rounded-lg break-words ${
                    msg.role === 'user' 
                      ? 'bg-blue-100 dark:bg-blue-900 text-gray-900 dark:text-white text-right ml-auto max-w-[85%]' 
                      : 'bg-indigo-50 dark:bg-gray-700 text-gray-900 dark:text-white text-left mr-auto max-w-[85%] relative pr-8'
                  }`}
                >
                  {msg.role === 'user' ? (
                    // User messages - simple text
                    <div className="whitespace-pre-wrap">
                      {msg.content}
                    </div>
                  ) : (
                    // Assistant messages - use SmartMarkdown
                    <SmartMarkdown 
                      content={msg.content} 
                      enableCollapsibleLists={true}
                      minItemsForCollapse={2}
                      attachments={msg.metadata?.attachments || []}
                    />
                  )}
                  {msg.role !== 'user' && (
                    <button
                      onClick={() => handleCopyMessage(index, msg.content)}
                      className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white transition-colors"
                      title={copiedMessageIndex === index ? 'Copied!' : 'Copy'}
                      aria-label="Copy message"
                    >
                      {copiedMessageIndex === index ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414l2.293 2.293 6.543-6.543a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              )}
              
              {/* Thinking Process */}
              {msg.role === 'assistant' && (msg.thinkingProcess || msg.reasoning) && (
                <ThinkingProcess 
                  thinkingProcess={msg.thinkingProcess} 
                  reasoning={msg.reasoning} 
                />
              )}
              
              {/* Action Approval UI */}
              {msg.role === 'assistant' && msg.metadata?.requiresApproval && msg.proposedActions && (
                <ActionApproval
                  proposedActions={msg.proposedActions}
                  onApprove={async (approvedActions) => {
                    setIsExecutingActions(true);
                    try {
                      const response = await axios.post('/api/ai/chat/execute-actions', {
                        actions: approvedActions,
                        originalMessage: pendingActions?.originalMessage || msg.content || '',
                        options: {
                          agentType: 'crmAssistant',
                          channel: 'web'
                        }
                      });

                      // Check if any actions failed
                      const hasFailures = response.data.results?.some(r => !r.success) || response.data.errors?.length > 0;
                      const allSuccessful = response.data.results?.every(r => r.success) && !response.data.errors?.length;
                      
                      // Build detailed result message
                      let resultMessage = response.data.summary || '';
                      if (response.data.results && response.data.results.length > 0) {
                        const resultDetails = response.data.results.map(r => {
                          if (r.success) {
                            return `✅ ${r.message || `${r.name} completed`}`;
                          } else {
                            return `❌ ${r.name} failed: ${r.error || r.message || 'Unknown error'}`;
                          }
                        }).join('\n');
                        resultMessage = resultDetails + (resultMessage ? '\n\n' + resultMessage : '');
                      }

                      // Update the message with execution results
                      setMessages(prevMessages => 
                        prevMessages.map((m, i) => 
                          i === index 
                            ? {
                                ...m,
                                metadata: {
                                  ...m.metadata,
                                  requiresApproval: false,
                                  executionResults: response.data.results,
                                  executionErrors: response.data.errors,
                                  executionSummary: response.data.summary,
                                  executionStatus: allSuccessful ? 'success' : hasFailures ? 'partial' : 'unknown'
                                },
                                content: resultMessage || m.content
                              }
                            : m
                        )
                      );
                      setPendingActions(null);
                      
                      // Check if we need to plan next action (sequential approval)
                      // If the executed action was a query/search, we should plan the next action
                      const executedAction = approvedActions[0];
                      if (executedAction && executedAction.name === 'get_entity_data' && allSuccessful) {
                        // Get the search results from execution
                        const searchResult = response.data.results?.find(r => r.name === 'get_entity_data');
                        if (searchResult && searchResult.success && searchResult.result?.data) {
                          // Plan next action with search results
                          try {
                            const originalMessage = pendingActions?.originalMessage || msg.content || '';
                            const planResponse = await axios.post('/api/ai/chat/plan', {
                              message: `${originalMessage}\n\n[Search results available: Found ${searchResult.result.count} ${searchResult.result.entityType}${searchResult.result.count !== 1 ? 's' : ''}. Please plan the next action using this data.]`,
                              history: messages.slice(0, index + 1).map(m => ({
                                role: m.role,
                                content: m.content
                              })),
                              options: {
                                agentType: 'crmAssistant',
                                channel: 'web',
                                previousActionResults: searchResult.result // Pass search results to planning
                              }
                            });

                            // If there are more actions to propose, show them
                            if (planResponse.data.proposedActions && planResponse.data.proposedActions.length > 0) {
                              const nextActionMessage = {
                                role: 'assistant',
                                content: planResponse.data.summary || 'I found the data. Here\'s the next step:',
                                thinkingProcess: planResponse.data.thinkingProcess,
                                reasoning: planResponse.data.reasoning,
                                proposedActions: planResponse.data.proposedActions,
                                metadata: {
                                  ...planResponse.data.metadata,
                                  context_sources: null,
                                  requiresApproval: true,
                                  isSequentialAction: true
                                },
                                ragSources: null
                              };
                              
                              setMessages(prevMessages => {
                                const newMessages = [...prevMessages, nextActionMessage];
                                setPendingActions({
                                  messageIndex: newMessages.length - 1,
                                  actions: planResponse.data.proposedActions,
                                  originalMessage: originalMessage
                                });
                                return newMessages;
                              });
                            }
                          } catch (planError) {
                            console.error('Failed to plan next action:', planError);
                            // Continue without planning next action
                          }
                        }
                      }
                      
                      // Show error alert if there were failures
                      if (hasFailures) {
                        const failedActions = response.data.results?.filter(r => !r.success) || [];
                        const errorMessages = failedActions.map(r => `${r.name}: ${r.error || 'Unknown error'}`).join('\n');
                        alert(`Some actions failed:\n\n${errorMessages}`);
                      }
                    } catch (error) {
                      console.error('Failed to execute actions:', error);
                      alert('Failed to execute actions. Please try again.');
                    } finally {
                      setIsExecutingActions(false);
                    }
                  }}
                  onCancel={() => {
                    setPendingActions(null);
                    setMessages(prevMessages => 
                      prevMessages.map((m, i) => 
                        i === index 
                          ? {
                              ...m,
                              metadata: {
                                ...m.metadata,
                                requiresApproval: false
                              }
                            }
                          : m
                      )
                    );
                  }}
                  isExecuting={isExecutingActions}
                />
              )}
              
              {/* Direct Actions Results (executed automatically) */}
              {msg.role === 'assistant' && msg.directActions && msg.directActions.length > 0 && (
                <>
                  {/* Show error messages for failed actions */}
                  {msg.directActions
                    .filter(a => !a.executed && (a.error || a.result?.error || a.result?.message))
                    .map((action, idx) => (
                      <div key={`error-${idx}`} className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <div className="text-sm font-semibold text-red-900 dark:text-red-200 mb-1">
                          ⚠️ {action.name} - Action cannot be done due to error
                        </div>
                        <div className="text-xs text-red-700 dark:text-red-300">
                          {action.error || action.result?.error || action.result?.message}
                        </div>
                      </div>
                    ))}
                  
                  {/* Count Results Display for count_entities results from directActions */}
                  {msg.directActions
                    .filter(a => a.executed && a.name === 'count_entities' && (a.result?.result || a.result?.count !== undefined))
                    .map((action, idx) => {
                      // Handle both result structures: wrapped in result.result or direct result
                      const countData = action.result?.result || action.result;
                      const entityType = countData?.entityType || 'entity';
                      const count = countData?.count || 0;
                      const entityTypeDisplay = entityType.charAt(0).toUpperCase() + entityType.slice(1);
                      return (
                        <div key={`count-direct-${idx}`} className="mt-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <div className="text-lg font-semibold text-green-900 dark:text-green-200">
                            📊 Count Result: {count} {entityTypeDisplay}{count !== 1 ? 's' : ''}
                          </div>
                        </div>
                      );
                    })}
                  
                  {/* Entity Data Display for get_entity_data results from directActions */}
                  {msg.directActions
                    .filter(a => a.executed && a.name === 'get_entity_data' && a.result?.result)
                    .map((action, idx) => {
                      const entityType = action.result.result.entityType;
                      const data = action.result.result.data || [];
                      const totalCount = action.result.result.count || data.length;
                      
                      // Show cards if 0 or < 5 items, show list if 5-20 items
                      const filterColumns = action.result?.result?.filterColumns || [];
                      const filterInfo = action.result?.result?.filterInfo || [];
                      const displayFields = action.result?.result?.displayFields || [];
                      
                      // Always show EntityDataCards for empty results (count === 0) or < 5 items
                      if (totalCount === 0 || totalCount < 5) {
                        return (
                          <EntityDataCards
                            key={`direct-action-${idx}`}
                            entityType={entityType}
                            data={data}
                            count={totalCount}
                            filterColumns={filterColumns}
                            filterInfo={filterInfo}
                            displayFields={displayFields}
                          />
                        );
                      } else {
                        return (
                          <EntityDataList
                            key={`direct-action-${idx}`}
                            entityType={entityType}
                            data={data}
                            totalCount={totalCount}
                            filterColumns={filterColumns}
                            filterInfo={filterInfo}
                            displayFields={displayFields}
                          />
                        );
                      }
                    })
                  }
                </>
              )}
              
              {/* Execution Results */}
              {msg.role === 'assistant' && msg.metadata?.executionResults && (
                <>
                  <ActionExecutionResults
                    executionResults={msg.metadata.executionResults}
                    errors={msg.metadata.executionErrors || []}
                  />
                  
                  {/* Count Results Display for count_entities results */}
                  {msg.metadata.executionResults
                    .filter(r => r.success && r.name === 'count_entities' && (r.result?.count !== undefined || r.result?.result?.count !== undefined))
                    .map((result, idx) => {
                      // Handle both result structures: direct result or wrapped result
                      const countData = result.result?.count !== undefined ? result.result : result.result?.result ? result.result.result : null;
                      const entityType = countData?.entityType || 'entity';
                      const count = countData?.count || 0;
                      const entityTypeDisplay = entityType.charAt(0).toUpperCase() + entityType.slice(1);
                      return (
                        <div key={`count-exec-${idx}`} className="mt-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <div className="text-lg font-semibold text-green-900 dark:text-green-200">
                            📊 Count Result: {count} {entityTypeDisplay}{count !== 1 ? 's' : ''}
                          </div>
                        </div>
                      );
                    })}
                  
                  {/* Entity Data Display for get_entity_data results */}
                  {msg.metadata.executionResults
                    .filter(r => r.success && r.name === 'get_entity_data' && (r.result?.data !== undefined || r.result?.result?.data !== undefined))
                    .map((result, idx) => {
                      // Handle both result structures: direct result or wrapped result
                      const entityData = result.result?.data ? result.result : result.result?.result ? result.result.result : null;
                      const entityType = entityData?.entityType;
                      const data = entityData?.data || [];
                      const totalCount = entityData?.count || data.length;
                      const filterColumns = entityData?.filterColumns || [];
                      const filterInfo = entityData?.filterInfo || [];
                      const displayFields = entityData?.displayFields || [];
                      
                      // Always show EntityDataCards for empty results (count === 0) or < 5 items
                      if (totalCount === 0 || totalCount < 5) {
                        return (
                          <EntityDataCards
                            key={idx}
                            entityType={entityType}
                            data={data}
                            count={totalCount}
                            filterColumns={filterColumns}
                            filterInfo={filterInfo}
                            displayFields={displayFields}
                          />
                        );
                      } else {
                        return (
                          <EntityDataList
                            key={idx}
                            entityType={entityType}
                            data={data}
                            totalCount={totalCount}
                            filterColumns={filterColumns}
                            filterInfo={filterInfo}
                            displayFields={displayFields}
                          />
                        );
                      }
                    })
                  }
                </>
              )}
              
              {/* Related Links */}
              {msg.role === 'assistant' && msg.metadata?.context_sources && renderRelatedLinks(msg.metadata.context_sources)}
            </div>
          );
        })}
        {isLoading && (
          <div className="text-center text-gray-500 dark:text-gray-400 py-2">
            <div className="inline-flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {getLocalizedMessages().thinkingMessage}
            </div>
          </div>
        )}
      </div>
      
      {/* Input Area */}
      <div className="p-4 border-t border-cyan-200 dark:border-cyan-800 flex-shrink-0">
        <div className="flex">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            className="flex-grow border border-gray-300 dark:border-gray-600 rounded-l-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 resize-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            placeholder={getLocalizedMessages().inputPlaceholder}
            disabled={isLoading}
          />
          <button 
            onClick={handleSend} 
            className="bg-blue-600 dark:bg-blue-500 text-white px-4 rounded-r-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-blue-300 dark:disabled:bg-blue-800 transition-colors flex items-center" 
            disabled={isLoading || input.trim() === ''}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>

      {/* Line QR Code Modal */}
      {showLineQrModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                </svg>
                <span>Line SalesBuddy</span>
              </h3>
              <button
                onClick={() => setShowLineQrModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {(aiConfig?.lineChannels?.salesBuddy || aiConfig?.lineChannels?.powerAssistant)?.qrCodeUrl ? (
              <div className="text-center">
                {/* Line Profile Information */}
                {lineProfile && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700 mb-4">
                    <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-3 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                      </svg>
                      Line Official Account Profile
                    </h4>
                    <div className="flex items-center justify-center space-x-4">
                      {/* Profile Picture */}
                      {lineProfile.pictureUrl && (
                        <div className="flex-shrink-0">
                          <img
                            src={lineProfile.pictureUrl}
                            alt="Line Profile"
                            className="w-16 h-16 rounded-full border-2 border-blue-300 dark:border-blue-600 object-cover"
                          />
                        </div>
                      )}
                      
                      {/* Profile Details */}
                      <div className="text-left">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                              {lineProfile.displayName || 'Unknown Name'}
                            </span>
                            {lineProfile.statusMessage && (
                              <span className="text-xs text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded-full">
                                {lineProfile.statusMessage}
                              </span>
                            )}
                          </div>
                          
                          {lineProfile.userId && (
                            <p className="text-xs text-blue-700 dark:text-blue-300">
                              User ID: {lineProfile.userId}
                            </p>
                          )}
                          
                          {lineProfile.language && (
                            <p className="text-xs text-blue-700 dark:text-blue-300">
                              Language: {lineProfile.language}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Connection Status */}
                    <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-600">
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-xs text-green-700 dark:text-green-300 font-medium">
                          Line OA Connected Successfully
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Loading Line Profile */}
                {lineProfileLoading && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700 mb-4">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      <span className="text-sm text-blue-700 dark:text-blue-300">
                        Loading Line Profile...
                      </span>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-4">
                  <img
                    src={(aiConfig.lineChannels.salesBuddy || aiConfig.lineChannels.powerAssistant)?.qrCodeUrl}
                    alt="Line QR Code"
                    className="w-48 h-48 mx-auto object-contain"
                  />
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Scan this QR code with your Line app to add the SalesBuddy Official Account
                </p>
                {(aiConfig?.lineChannels?.salesBuddy || aiConfig?.lineChannels?.powerAssistant)?.lineId && (
                  <p className="text-sm text-gray-500 dark:text-gray-500">
                    Line ID: <span className="font-mono text-green-600 dark:text-green-400">{(aiConfig.lineChannels.salesBuddy || aiConfig.lineChannels.powerAssistant)?.lineId}</span>
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 dark:text-gray-500 mb-4">
                  <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">No QR Code Available</p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  QR code has not been uploaded yet. Please contact your administrator.
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowLineQrModal(false)}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesBuddy;